import type { ExternalTaskConcrete, TaskGenerator, TaskGeneratorNew, TaskTemplate } from '../types/compliance.ts';

// applicableStandard is not included because it maps to any of the blessed templates:
export type ExternalTaskTypesWithFixedTemplates = Exclude<ExternalTaskConcrete['type'], 'applicableStandard'>;

export const externalTaskTypeToTemplateID: {
  [Key in ExternalTaskTypesWithFixedTemplates]: TaskTemplate['taskTemplateID'];
} = {
  permitIndexing: 'template_mf383ohf_00000000000',
  permitCondition: 'template_mf46r403_00000000000',
  permitsReviewConditionalConditions: 'template_mffu8ryd_00000000000',
  determinationComplianceIssue: 'template_lm9t8ksr_00000000000',
  hazWasteAccumulationDeadline: 'template_mhk2x7qa_00000000000',
  hazWasteStreamReview: 'template_mhm4v9cs_00000000000',
  hazWasteContainerInspection: 'template_mhp7k2wd_00000000000',
  hazWasteTankInspection: 'template_mhp7k3tn_00000000000',
  hazWasteInspectionFinding: 'template_mhs4f8qe_00000000000',
};

export const externalTaskTypeToIdPrefix: {
  [Key in ExternalTaskConcrete['type']]: ExternalTaskConcrete['externalTaskID'];
} = {
  permitIndexing: 'external_task_permit_indexing_',
  permitCondition: 'external_task_permit_condition_',
  permitsReviewConditionalConditions: 'external_task_permits_review_conditional_conditions_',
  applicableStandard: 'external_task_determination_standard_task_',
  determinationComplianceIssue: 'external_task_determination_compliance_issue_',
  hazWasteAccumulationDeadline: 'external_task_hazwaste_accumulation_deadline_',
  hazWasteStreamReview: 'external_task_hazwaste_stream_review_',
  hazWasteContainerInspection: 'external_task_hazwaste_container_inspection_',
  hazWasteTankInspection: 'external_task_hazwaste_tank_inspection_',
  hazWasteInspectionFinding: 'external_task_hazwaste_inspection_finding_',
};

export const externalTaskFriendlyTypeMap: Record<ExternalTaskConcrete['type'], string> = {
  permitIndexing: 'Index Permit',
  permitCondition: 'Permit Condition',
  permitsReviewConditionalConditions: 'Review Conditional Permit Conditions',
  applicableStandard: 'Applicability Tool Applicable Task',
  determinationComplianceIssue: 'Applicability Tool Compliance Issue',
  hazWasteAccumulationDeadline: 'HazWaste Accumulation Deadline',
  hazWasteStreamReview: 'HazWaste Waste Stream Review',
  hazWasteContainerInspection: 'HazWaste Weekly Container Inspection',
  hazWasteTankInspection: 'HazWaste Daily Tank Inspection',
  hazWasteInspectionFinding: 'HazWaste Inspection Corrective Action',
};

const prefixesSortedLongest = Object.entries(externalTaskTypeToIdPrefix).sort((a, b) => b[1].length - a[1].length);

// An external task is said to "drive" a task generator if it is the only linked external task. "Drive" here means
// that the external task controls whether task is considered completed, and possibly affects the schedule as well:
export function taskGeneratorIsDrivenByExternalTask(
  generator: TaskGenerator | TaskGeneratorNew | null | undefined,
): null | {
  type: Exclude<
    ExternalTaskTypesWithFixedTemplates,
    'applicableStandard' | 'determinationComplianceIssue' | 'permitCondition' | 'permitsReviewConditionalConditions'
  >;
  externalTaskID: ExternalTaskConcrete['externalTaskID'];
} {
  if (!generator?.externalTaskIDs || generator.externalTaskIDs.length !== 1) {
    return null;
  }

  const externalTaskID = generator.externalTaskIDs[0];
  for (const [type, prefix] of prefixesSortedLongest as [
    ExternalTaskConcrete['type'],
    ExternalTaskConcrete['externalTaskID'],
  ][]) {
    // These are driven by the authoritative Compliance Tool templates, not the external task mechanisms:
    if (type === 'applicableStandard') {
      continue;
    }

    // The expectation here is that the task to resolve the compliance issue may be recurring (i.e. the compliance
    // issue is that you are not performing some recurring task), so resolving the compliance issue in the Determination
    // Tool should not cause the task series to finish:
    if (type === 'determinationComplianceIssue') {
      continue;
    }

    // These are driven by the required conditions of the permit as indexed by the user, and therefore can never
    // be driven by an external task:
    if (type === 'permitCondition') {
      continue;
    }

    // While the existence of conditional conditions could reasonably be considered something driving this task,
    // I think it's most sensible to leave it to human discretion when the task is complete:
    if (type === 'permitsReviewConditionalConditions') {
      continue;
    }

    if (externalTaskID.startsWith(prefix)) {
      return { type, externalTaskID };
    }
  }

  return null;
}

export function taskGeneratorExternalTaskIdsOfType(
  generator: TaskGenerator | TaskGeneratorNew | null | undefined,
  externalTaskType: ExternalTaskConcrete['type'],
): ExternalTaskConcrete['externalTaskID'][] {
  if (!generator?.externalTaskIDs || generator.externalTaskIDs.length === 0) {
    return [];
  }

  const prefix = externalTaskTypeToIdPrefix[externalTaskType];
  const result: ExternalTaskConcrete['externalTaskID'][] = [];

  for (const externalTaskID of generator.externalTaskIDs) {
    if (externalTaskID.startsWith(prefix)) {
      result.push(externalTaskID);
    }
  }

  return result;
}

export function externalTaskIdToType(id: ExternalTaskConcrete['externalTaskID']): ExternalTaskConcrete['type'] | null {
  for (const [type, prefix] of Object.entries(externalTaskTypeToIdPrefix)) {
    if (id.startsWith(prefix)) {
      return type as ExternalTaskConcrete['type'];
    }
  }

  return null;
}
